import React, { useCallback, useRef, useState, forwardRef, useImperativeHandle } from 'react';
import { useIntl } from 'umi';
import { Upload, Text, Form, Button, Icon, Modal } from 'tea-component';
import classNames from 'classnames';
import useCos from '@gp/hooks/useCos';
import moment from 'moment';
import http from '@/utils/http';
import FileUploader from '../FileUploader';
import { 
  IUploadFileProps, 
  IUploadFileRef, 
  TStatus, 
  IProgressEvent 
} from './types';
import './index.scss';

const MAX_SIZE_TXT_CSV = 500 * 1024 * 1024; // 500MB
const MAX_SIZE_EXCEL = 100 * 1024 * 1024; // 100MB

const defaultAccept = ['.csv', '.txt', '.xlsx', '.xls'];

const formatFileSize = (v: number): string => {
  if (!v) return '0B';
  const g = Math.round(v / (1024 * 1024 * 1024));
  const m = Math.round(v / (1024 * 1024));
  const k = Math.round(v / 1024);
  if (g >= 1) return `${g}GB`;
  if (m >= 1) return `${m}MB`;
  if (k >= 1) return `${k}KB`;
  return `${v}B`;
};

const UploadFile = forwardRef<IUploadFileRef, IUploadFileProps>(({
  accept = defaultAccept,
  tips,
  failText,
  onSuccess,
  onError,
  onDelete,
  defaultState,
}, ref) => {
  const { formatMessage } = useIntl();
  const { putObject, deleteObject, getObjectUrl, cancelTask } = useCos({qCouldService: fetchCOSInfo, isUseAccelerate: false});

  // 修改：使用默认值初始化状态
  const [status, setStatus] = useState<TStatus>(defaultState?.mode === 'csv' || defaultState?.mode === 'excel' ? 'success' : 'idle');
  const [fileUploadError, setFileUploadError] = useState<React.ReactNode>(failText);
  const [file, setFile] = useState<File | null>(defaultState?.file || null);
  const [percent, setPercent] = useState<number>(defaultState?.file ? 100 : 0);
  const [uploadDate, setUploadDate] = useState<string>(defaultState?.uploadDate || '');
  const [deleteLoading, setDeleteLoading] = useState<boolean>(false);
  const taskIdRef = useRef<string | null>(null);
  const uploadedKeyRef = useRef<string | null>(defaultState?.fileUrlInfo?.path || null);
  
  const getExt = (file: File): string => {
    const dot = file.name.lastIndexOf('.');
    return dot >= 0 ? file.name.substring(dot + 1).toLowerCase() : '';
  };

  const validateSizeByType = (file: File): { ok: boolean; message?: string; } => {
    // 文件不能为空
    if (file.size === 0) {
      return {
        ok: false,
        message: formatMessage({id: 'dbl_personal_data_file_empty'}),
      };
    }
    // 文件类型和大小限制
    const ext = getExt(file);
    if (!accept.includes(`.${ext}`)) {
      return {
        ok: false,
        message: formatMessage({ id: 'dbl_personal_data_wrong_file_type' })
      };
    }
    if (ext === 'csv' || ext === 'txt') {
      if (file.size > MAX_SIZE_TXT_CSV) {
        return { 
          ok: false, 
          message: formatMessage({ id: 'dbl_personal_data_file_exceeds_500mb' }) 
        };
      }
      return { ok: true };
    }
    if (ext === 'xlsx' || ext === 'xls') {
      if (file.size > MAX_SIZE_EXCEL) {
        return { 
          ok: false, 
          message: formatMessage({ id: 'dbl_personal_data_file_exceeds_100mb' }) 
        };
      }
      return { ok: true };
    }
    return { ok: true };
  };
  const beforeUpload = (file: File): boolean => {
    const { ok, message } = validateSizeByType(file);
    if (!ok) {
      setFileUploadError(message);
      setStatus('error');
      setFile(file);
      setUploadDate(moment().format('YYYY-MM-DD HH:mm:ss'));
      return false;
    }
    return ok;
  };
  const resetState = (): void => {
    setStatus('idle');
    setFile(null);
    setPercent(0);
    setUploadDate('');
    taskIdRef.current = null;
    uploadedKeyRef.current = null;
  };

  const uploadToCos = useCallback(async (targetFile: File): Promise<void> => {
    const uploadDate = moment().format('YYYY-MM-DD HH:mm:ss');
    setUploadDate(uploadDate);
    setStatus('validating');
    setPercent(0);
    const time = Date.now();
    const key = `local_file/${time}_${targetFile.name}`;
    uploadedKeyRef.current = key;
    try {
      await putObject({
        path: key,
        file: targetFile,
        onProgress: ({ percent: p }: IProgressEvent) => {
          const real = Math.round((p || 0) * 100);
          setPercent(real);
          if (p < 1 && status !== 'validating') {
            setStatus('validating');
          }
        },
        onTaskReady: (tid: string) => {
          taskIdRef.current = tid;
        },
      });

      const urlInfo = await getObjectUrl({ path: key }).catch(() => null);
      setStatus('success');
      setPercent(100);
      onSuccess?.({ ...urlInfo, path: key }, targetFile);
    } catch (e) {
      setStatus('error');
      setFileUploadError(failText);
      onError?.(e, targetFile);
    }
  }, [getObjectUrl, onError, onSuccess, putObject, status]);

  const handleAbort = (): void => {
    if (taskIdRef.current) {
      cancelTask(taskIdRef.current).then(() => {
        resetState();
      }).catch((e: Error) => {
        console.error('文件上传错误', e);
      });
    }
  };

  const retryUpload = (): void => {
    if (!file) return;
    const isOk = beforeUpload(file);
    isOk && uploadToCos(file);
  };
  const handleDelete = (): void => {
    const key = uploadedKeyRef.current;
    if (key) {
      deleteObject({ path: key }).then(() => {
        resetState();
        onDelete?.();
      }).catch((e: Error) => {
        console.error('删除上传的文件错误', e);
      }).finally(() => {
        setDeleteLoading(false);
      });
    }
  };
  
  // 上传文件成功后的删除按钮
  const handleSuccessDelete = async (): Promise<void> => {
    try {
      const result = await Modal.confirm({
        message: formatMessage({id: 'dbl_personal_data_delete_title'}),
        description: formatMessage({id: 'dbl_personal_data_delete_content'}),
        okText: formatMessage({id: 'dbl_common_confirm'}),
        cancelText: formatMessage({id: 'dbl_common_cancel'}),
        style: {zIndex: 1010},
        onOk: () => {
          setDeleteLoading(true);
        }
      });
      // 用户点击了确认按钮
      if (result) {
        handleDelete();
      }
      
    } catch(e) {
      console.log("文件删除错误 ", e);
    }
  };
  // 将handleDelete方法暴露到ref中
  useImperativeHandle(ref, () => ({
    deleteFile: handleDelete,
    status,
    uploadFileTaskId: uploadedKeyRef.current,
  }), [status]);

  const showFileDetail = !!file && (status === 'validating' || status === 'error' || status === 'success');

  return (
    <Form.Control
      className={classNames({
        'dbl-upload-file--dragging': false,
        'dbl-upload-file--uploading': status === 'validating',
        'dbl-upload-file--success': status === 'success',
        'dbl-upload-file--fail': status === 'error',
      })}
      message={status === 'error' ? (<Text theme="danger">{fileUploadError}</Text>) : tips}
    >
      <FileUploader
        accept={accept}
        multiple={false}
        beforeUpload={beforeUpload}
        onUploadRequest={(file: File): void => {
          setFile(file);
          uploadToCos(file);
        }}
        onError={(errorMsg, file) => {
          let msg = errorMsg;
          if (errorMsg.includes('文件类型不支持')) {
            msg = formatMessage({ id: 'dbl_personal_data_wrong_file_type' });
          }
          setFileUploadError(msg);
          setStatus('error');
          setFile(file);
          setUploadDate(moment().format('YYYY-MM-DD HH:mm:ss'));
        }}
      >
        {({ open, isDragging }: { open: () => void; isDragging: boolean; }) => (
          <Upload.Dragger
            className="dbl-upload-file__dragger"
            filename={
              file && (
                <div className="dbl-upload-file__filename">
                  <span className="dbl-upload-file__name">{file.name}</span>
                  {status === 'validating' ? (
                    <Icon type="loading" />
                  ) : status === 'success' ? (
                    <Icon type="success" />
                  ) : status === 'error' ? (
                    <Icon type="error" />
                  ) : null}
                  {status === 'validating' && typeof percent === 'number' ? ` ${percent}%` : ''}
                </div>
              )
            }
            description={
              showFileDetail ? (
                <div className="dbl-upload-file__desc">
                  <p>{formatMessage({ id: 'dbl_personal_data_file_size' })}: {formatFileSize(file!.size)}</p>
                  <p>{formatMessage({ id: 'dbl_personal_data_upload_date' })}: {uploadDate || '-'}</p>
                </div>
              ) : undefined
            }
            button={
              (status === 'validating' && taskIdRef.current) ? (
                <Button type="link" onClick={handleAbort}>
                  {formatMessage({ id: 'dbl_personal_data_cancel_upload' })}
                </Button>
              ) : status === 'error' ? (
                <>
                  <Button type="link" onClick={retryUpload}>
                    {formatMessage({ id: 'dbl_personal_data_re_upload' })}
                  </Button>
                  <Button type="link" style={{ marginLeft: 8 }} onClick={() => {
                    resetState();
                  }}>
                    {formatMessage({ id: 'dbl_personal_data_delete' })}
                  </Button>
                </>
              ) : status === 'success' ? (
                <Button type="link" onClick={() => {
                  !deleteLoading && handleSuccessDelete();
                }}>
                  {
                    deleteLoading ? (<Icon type="loading" />) : formatMessage({ id: 'dbl_personal_data_delete' })
                  }
                </Button>
              ) : undefined
            }
          >
            {isDragging ? (
              formatMessage({ id: 'dbl_personal_data_release_mouse' })
            ) : (
              <>
                <a onClick={open}>{formatMessage({ id: 'dbl_personal_data_click_to_upload' })}</a>
                <Text theme="weak"> / {formatMessage({ id: 'dbl_personal_data_drag_to_area' })}</Text>
              </>
            )}
          </Upload.Dragger>
        )}
      </FileUploader>
    </Form.Control>
  );
});

UploadFile.displayName = 'UploadFile';

export default UploadFile;

// 获取COS相关配置，及即将上传的视频的uuid、上传路径
const fetchCOSInfo = async () => http({
  method: 'GET',
  url: '/api/v1/aigc/tencent_qcloud/auth',
}) as any;